/**
 * KtsPhasedMount — phase-aware kinetic capture component.
 *
 * Drop-in replacement for the legacy KtsMount.tsx for kinetic tools
 * registered with phased lifecycle support (AiTool.phases populated).
 *
 * Each backend phase boundary maps to a distinct UI surface:
 *   idle              → Run button
 *   tollbooth_pending → "Securing session..." spinner
 *   prework           → "Get into position" + countdown + Ready button
 *   ready             → "Ready" + Start button
 *   action_active     → Workout UI: timer, rep counter, Stop button
 *   action_closing    → Closing slot...
 *   wind_down         → "Computing your SmartBlock..." with polling progress
 *   complete          → Results screen (verified reps, gec, integrity)
 *   failed/abandoned  → Error + retry/abandon
 *
 * Phase 4a: structural skeleton with all phases wired but minimal UI.
 *           Visual polish comes after end-to-end validation.
 *           Pose detection / MediaPipe integration: reuses primitives
 *           from the legacy KtsMount where applicable.
 *
 * The component itself is intentionally thin — most logic lives in
 * useKtsPhasedSession (state machine + REST orchestration).
 */

import { useEffect, useRef, useState } from 'react';
import { useKtsPhasedSession, type FrontendStatus } from '../../lib/use-kts-phased-session';
import type { CanonicalActionEvent, SessionMode } from '../../lib/kts-v1-client';
import { useKineticCapture } from '../../hooks/useKineticCapture';
import type { HeuristicRule } from '../../lib/heuristic-engine';

export interface KtsPhasedMountProps {
  toolId: string;
  authToken: string;
  /** Optional preferred mode — falls back to tool's default. */
  mode?: SessionMode;
  /** For coop: the partner's user_id (primary supplies; partner discovers via session link). */
  partnerId?: string;
  /** Total session duration for action phase (e.g., 60 for AMRAP-60). */
  durationS?: number;
  /** Tool's heuristic_rule (joint angles + thresholds). When provided, the
   *  action phase runs MediaPipe pose detection + rep counting and emits
   *  reps automatically. When omitted, falls back to the demo "Tap to log
   *  rep" button (keeps the manual-test path alive). */
  heuristicRule?: HeuristicRule | null;
  /** Callback when session completes successfully. */
  onComplete?: (result: {
    sessionId: string;
    primaryYield: number | null;
    durationS: number | null;
    gec0: number | null;
    integrityScore: number | null;
    merkleRoot: string | null;
  }) => void;
  /** Callback when session fails or is abandoned. */
  onClosed?: (reason: 'failed' | 'abandoned', failureReason?: string | null) => void;
}

export function KtsPhasedMount(props: KtsPhasedMountProps) {
  const {
    toolId,
    authToken,
    mode,
    partnerId,
    durationS = 60,
    heuristicRule,
    onComplete,
    onClosed,
  } = props;

  const phased = useKtsPhasedSession({
    toolId,
    authToken,
    mode,
    partnerId,
    partnerRole: partnerId ? 'filmer' : undefined, // simplest default for coop pairs
  });

  // Fire callbacks on terminal transitions
  useEffect(() => {
    if (!phased.session) return;
    if (phased.status === 'complete') {
      onComplete?.({
        sessionId: phased.session.session_id,
        primaryYield: phased.session.primary_yield,
        durationS: phased.session.duration_s,
        gec0: phased.session.gec?.gec0 ?? null,
        integrityScore: phased.session.integrity_score,
        merkleRoot: phased.session.merkle_root,
      });
    } else if (phased.status === 'failed' || phased.status === 'abandoned') {
      onClosed?.(phased.status, phased.session.failure_reason);
    }
  }, [phased.status, phased.session, onComplete, onClosed]);

  return (
    <div className="kts-phased-mount">
      <PhaseSurface phased={phased} durationS={durationS} heuristicRule={heuristicRule ?? null} />
      {phased.error && (
        <div className="kts-phased-error" role="alert">
          <strong>{phased.error.code}</strong>: {phased.error.message}
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Per-phase UI surfaces — keep simple; swap to designer-built components later
// ───────────────────────────────────────────────────────────────────────────

function PhaseSurface({
  phased,
  durationS,
  heuristicRule,
}: {
  phased: ReturnType<typeof useKtsPhasedSession>;
  durationS: number;
  heuristicRule: HeuristicRule | null;
}) {
  const { status } = phased;

  switch (status) {
    case 'idle':
      return <IdleSurface phased={phased} />;

    case 'connecting':
    case 'tollbooth_pending':
      return <SpinnerSurface label="Securing session..." />;

    case 'prework':
      return <PreworkSurface phased={phased} />;

    case 'submitting_prework':
      return <SpinnerSurface label="Setting up..." />;

    case 'ready':
      return <ReadySurface phased={phased} />;

    case 'starting_action':
      return <SpinnerSurface label="Starting capture..." />;

    case 'action_active':
      return <ActionSurface phased={phased} durationS={durationS} heuristicRule={heuristicRule} />;

    case 'stopping_action':
    case 'action_closing':
      return <SpinnerSurface label="Closing your block..." />;

    case 'finalizing':
    case 'wind_down':
    case 'polling':
      return <WindDownSurface phased={phased} />;

    case 'complete':
      return <CompleteSurface phased={phased} />;

    case 'failed':
      return <FailedSurface phased={phased} />;

    case 'abandoned':
      return <AbandonedSurface phased={phased} />;

    default:
      return <SpinnerSurface label={`Status: ${status as string}`} />;
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Idle / Run
// ───────────────────────────────────────────────────────────────────────────

function IdleSurface({ phased }: { phased: ReturnType<typeof useKtsPhasedSession> }) {
  return (
    <div className="phase-idle">
      <p>Press <strong>Run</strong> to begin. This will deduct your TC and open the session.</p>
      <button type="button" onClick={() => void phased.start()}>
        Run
      </button>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Prework — get into position, calibrate
// ───────────────────────────────────────────────────────────────────────────

function PreworkSurface({ phased }: { phased: ReturnType<typeof useKtsPhasedSession> }) {
  const [countdown, setCountdown] = useState(15);
  const [confidence, setConfidence] = useState(0.85);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const handleSubmit = () => {
    void phased.submitPrework({
      calibration: {
        locked: confidence >= 0.5,
        confidence,
        // Real implementation: pull baseline from MediaPipe pose helper.
        baseline: { source: 'kts-phased-skeleton' },
      },
    });
  };

  return (
    <div className="phase-prework">
      <h3>Get into position</h3>
      <p>
        {countdown > 0
          ? `Frame yourself in the camera. Starting in ${countdown}s...`
          : 'Position locked. Confirm and continue.'}
      </p>
      <div>
        <label>
          Pose confidence:
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={confidence}
            onChange={(e) => setConfidence(Number(e.target.value))}
          />
          <span>{confidence.toFixed(2)}</span>
        </label>
      </div>
      <button type="button" onClick={handleSubmit} disabled={countdown > 0}>
        Confirm position
      </button>
      <button type="button" onClick={() => void phased.abandon('user_cancelled')}>
        Cancel
      </button>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Ready — about to start workout
// ───────────────────────────────────────────────────────────────────────────

function ReadySurface({ phased }: { phased: ReturnType<typeof useKtsPhasedSession> }) {
  return (
    <div className="phase-ready">
      <h3>Ready</h3>
      <p>Position locked. Press <strong>Start</strong> when you're set.</p>
      <button type="button" onClick={() => void phased.beginAction()}>
        Start
      </button>
      <button type="button" onClick={() => void phased.abandon('user_cancelled')}>
        Cancel
      </button>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Action — the workout
// ───────────────────────────────────────────────────────────────────────────

function ActionSurface({
  phased,
  durationS,
  heuristicRule,
}: {
  phased: ReturnType<typeof useKtsPhasedSession>;
  durationS: number;
  heuristicRule: HeuristicRule | null;
}) {
  const [elapsed, setElapsed] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Debug fallback — when ?ktsDebug=1 is in the URL OR no heuristicRule is
  // wired, show the manual "Tap to log rep" button so synthetic tests still
  // work without a camera.
  const debugMode =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('ktsDebug') === '1';
  const showManualButton = debugMode || !heuristicRule;

  // Capture pipeline — MediaPipe pose + heuristic engine. enabled=true means
  // camera + pose are active. On rep detection: phased.emitEvent fires the
  // canonical event into the buffered batch.
  const capture = useKineticCapture({
    enabled: true,
    rule: heuristicRule,
    onRep: (event) => {
      phased.emitEvent(event);
    },
    onPoseFrame: (landmarks) => {
      // Skeleton overlay — non-essential, swallow draw errors silently.
      const canvas = canvasRef.current;
      const video = capture.videoRef.current;
      if (!canvas || !video) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const connections = [
        [11, 13], [13, 15],     // left arm
        [12, 14], [14, 16],     // right arm
        [11, 12],               // shoulders
        [11, 23], [12, 24],     // torso
        [23, 24],               // hips
        [23, 25], [25, 27],     // left leg
        [24, 26], [26, 28],     // right leg
      ];
      ctx.strokeStyle = '#00ff88';
      ctx.lineWidth = 3;
      for (const [a, b] of connections) {
        const la = landmarks[a];
        const lb = landmarks[b];
        if (!la || !lb || (la.visibility ?? 0) < 0.3 || (lb.visibility ?? 0) < 0.3) continue;
        ctx.beginPath();
        ctx.moveTo(la.x * canvas.width, la.y * canvas.height);
        ctx.lineTo(lb.x * canvas.width, lb.y * canvas.height);
        ctx.stroke();
      }
    },
  });

  // Session timer
  useEffect(() => {
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // Auto-stop on timer expiry
  useEffect(() => {
    if (elapsed >= durationS) {
      void phased.finishAction({ duration_s: durationS, reason: 'timer_expired' });
    }
  }, [elapsed, durationS, phased]);

  const remaining = Math.max(0, durationS - elapsed);

  // Manual rep log — only used when in debug mode or no heuristic rule.
  const emitManualRep = () => {
    const event: CanonicalActionEvent = {
      action: 'rep_complete',
      timestamp_ms: Date.now(),
      score: 0.9,
      reinforced: true,
    };
    phased.emitEvent(event);
  };

  return (
    <div className="phase-action">
      <div
        className="action-camera"
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 720,
          // Reserve real estate even before the stream attaches. Without an
          // explicit aspect-ratio the <video> element collapses to 0px high
          // until the stream produces its first frame, making the camera
          // feel "missing" even when it's working.
          aspectRatio: '4 / 3',
          background: '#0a0a0a',
          borderRadius: 8,
          overflow: 'hidden',
          margin: '0 auto',
        }}
      >
        <video
          ref={capture.videoRef}
          autoPlay
          playsInline
          muted
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            // Mirror so user sees themselves naturally; landmarks come from
            // un-mirrored frame so heuristic angles still compute correctly.
            transform: 'scaleX(-1)',
            display: 'block',
          }}
        />
        <canvas
          ref={canvasRef}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            transform: 'scaleX(-1)',
          }}
        />
        {!capture.cameraReady && (
          <div
            className="action-camera-pending"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
              textAlign: 'center',
              color: '#fff',
              fontSize: 14,
            }}
          >
            {capture.cameraError ?? 'Starting camera…'}
          </div>
        )}
        {capture.cameraReady && capture.poseLoading && (
          <div
            className="action-camera-pending"
            style={{
              position: 'absolute',
              right: 8,
              top: 8,
              padding: '4px 8px',
              fontSize: 12,
              borderRadius: 4,
              background: 'rgba(0,0,0,0.6)',
              color: '#fff',
            }}
          >
            Loading pose model…
          </div>
        )}
      </div>

      <div className="action-status">
        <div className="action-timer">
          {String(Math.floor(remaining / 60)).padStart(2, '0')}:
          {String(remaining % 60).padStart(2, '0')}
        </div>
        <div className="action-counter">
          Reps: <strong>{capture.repCount}</strong>
          {' '}<span style={{ opacity: 0.6 }}>(buffered: {phased.bufferedEventCount})</span>
        </div>
        {capture.debugAngles && (
          <div className="action-debug" style={{ opacity: 0.6, fontSize: 12 }}>
            L:{capture.debugAngles.L}° R:{capture.debugAngles.R}° avg:{capture.debugAngles.avg}°
            {' · '}phase:{capture.repPhase}
          </div>
        )}
        {capture.poseError && (
          <div className="action-pose-error" role="alert">
            Pose model error: {capture.poseError}
          </div>
        )}
      </div>

      <div className="action-controls">
        {showManualButton && (
          <button type="button" onClick={emitManualRep}>
            {debugMode ? 'Tap to log rep (debug)' : 'Tap to log rep (no rule wired)'}
          </button>
        )}
        <button
          type="button"
          onClick={() =>
            void phased.finishAction({ duration_s: elapsed, reason: 'user_finished' })
          }
        >
          Stop
        </button>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Wind-down — async backend wind-down with polling
// ───────────────────────────────────────────────────────────────────────────

function WindDownSurface({ phased }: { phased: ReturnType<typeof useKtsPhasedSession> }) {
  const phaseCount = phased.session?.phase_receipts?.length ?? 0;

  // Auto-call /finish exactly once per mount when we land in action_closing.
  // Earlier version had `phased` in deps which caused the effect to re-run
  // on every render, potentially firing finalize() in flight multiple times
  // (each subsequent call hits 409 invalid_state once status moves past
  // action_closing). The ref pattern below guarantees a single call.
  const calledRef = useRef(false);
  const finalizeRef = useRef(phased.finalize);
  finalizeRef.current = phased.finalize;

  useEffect(() => {
    if (phased.status === 'action_closing' && !calledRef.current) {
      calledRef.current = true;
      console.log('[KtsPhasedMount] auto-firing /finish for session', phased.session?.session_id);
      void finalizeRef.current();
    }
    // Reset on status change away from action_closing/wind_down so a
    // subsequent run (rare — would require manual reset) re-arms.
    if (phased.status !== 'action_closing' && phased.status !== 'wind_down' && phased.status !== 'polling' && phased.status !== 'finalizing') {
      calledRef.current = false;
    }
  }, [phased.status, phased.session?.session_id]);

  return (
    <div className="phase-winddown">
      <h3>Computing your SmartBlock…</h3>
      <p>
        Sealing the session and computing your Merkle root. Receipts so far:{' '}
        <strong>{phaseCount}</strong>.
      </p>
      <p>You can leave this page — the session will complete in the background.</p>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Complete
// ───────────────────────────────────────────────────────────────────────────

function CompleteSurface({ phased }: { phased: ReturnType<typeof useKtsPhasedSession> }) {
  const s = phased.session;
  if (!s) return null;
  return (
    <div className="phase-complete">
      <h3>Session complete</h3>
      <ul>
        <li>Primary yield: <strong>{s.primary_yield ?? '—'}</strong></li>
        <li>Duration: <strong>{s.duration_s != null ? `${s.duration_s.toFixed(1)}s` : '—'}</strong></li>
        <li>GEC<sub>0</sub>: <strong>{s.gec?.gec0 != null ? s.gec.gec0.toFixed(3) : '—'}</strong></li>
        <li>Integrity score: <strong>{s.integrity_score != null ? s.integrity_score.toFixed(2) : '—'}</strong></li>
        <li>Merkle root: <code>{s.merkle_root?.slice(0, 16) ?? '—'}…</code></li>
        <li>Phases attested: <strong>{s.phase_receipts.filter((r) => r.attested).length}</strong> of {s.phase_receipts.length}</li>
      </ul>
      <button type="button" onClick={() => phased.reset()}>
        Run another
      </button>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Failed / Abandoned
// ───────────────────────────────────────────────────────────────────────────

function FailedSurface({ phased }: { phased: ReturnType<typeof useKtsPhasedSession> }) {
  const reason = phased.session?.failure_reason ?? phased.error?.message ?? 'Unknown error';
  return (
    <div className="phase-failed">
      <h3>Something went wrong</h3>
      <p>{reason}</p>
      <p><em>Your TC has been refunded. Press Retry to start a fresh session.</em></p>
      <button type="button" onClick={() => phased.reset()}>
        Retry
      </button>
    </div>
  );
}

function AbandonedSurface({ phased }: { phased: ReturnType<typeof useKtsPhasedSession> }) {
  return (
    <div className="phase-abandoned">
      <h3>Session ended</h3>
      <p>Session was cancelled. TC committed at tollbooth is non-refundable.</p>
      <button type="button" onClick={() => phased.reset()}>
        Run another
      </button>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Spinner
// ───────────────────────────────────────────────────────────────────────────

function SpinnerSurface({ label }: { label: string }) {
  return (
    <div className="phase-spinner" role="status">
      <p>{label}</p>
    </div>
  );
}

/** Re-export the FrontendStatus type for consumers that want to drive UI off it. */
export type { FrontendStatus };
